import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, CaretDown } from "@phosphor-icons/react";
import SupportMailLink from "@/components/SupportMailLink";

// Public FAQ page reachable from the top nav. Client-side search + category
// filter pills. Answers open with the same accordion pattern used elsewhere
// in the app (Check-in, "Approfondisci", …). Single-open behaviour.
// Deep-linkable: /faq?open=<faq_id> auto-expands that card and scrolls to
// it — used by "A bordo · Vita quotidiana" to jump into specific answers.
export default function FAQ() {
  const { t, lang } = useApp();
  const { search } = useLocation();
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [openId, setOpenId] = useState(null);
  const scrolledRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/faq?lang=${lang}`);
        setData(res.data);
      } catch { /* fail-soft */ }
    })();
  }, [lang]);

  // Consume ?open=<faq_id> once, after data is loaded. We open the card and
  // scroll it into view. `scrolledRef` prevents scrolling more than once per
  // navigation so the user's manual scroll isn't hijacked afterwards.
  useEffect(() => {
    if (!data || scrolledRef.current) return;
    const params = new URLSearchParams(search);
    const id = params.get("open");
    if (!id) return;
    const found = data.items.find(f => f.id === id);
    if (!found) return;
    setCategory("all");
    setQuery("");
    setOpenId(id);
    scrolledRef.current = true;
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="faq-card-${id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [data, search]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.items.filter(f => {
      if (category !== "all" && f.category !== category) return false;
      if (!q) return true;
      return f.question.toLowerCase().includes(q)
        || f.answer.toLowerCase().includes(q);
    });
  }, [data, query, category]);

  if (!data) {
    return <div className="p-8 text-ink-soft">{t.common.loading}</div>;
  }

  const pills = [{ id: "all", label: t.faq.filterAll }, ...data.categories];

  return (
    <div className="max-w-4xl mx-auto px-5 py-8 space-y-8">
      <div>
        <h1
          data-testid="faq-title"
          className="font-display font-black text-3xl sm:text-4xl text-ink tracking-tight"
        >
          {t.faq.title}
        </h1>
        <p className="text-ink-soft mt-2 leading-relaxed max-w-2xl">
          {t.faq.subtitle}
        </p>
        <p
          data-testid="faq-timing-disclaimer"
          className="text-xs sm:text-sm text-ink-soft/70 mt-4 leading-relaxed max-w-2xl font-sans"
        >
          {t.common.timingDisclaimer}
          {" "}
          <SupportMailLink testid="faq-support-link" subject="Segnalazione — FAQ" />
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlass
          size={18}
          weight="duotone"
          className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-soft/70 pointer-events-none"
          aria-hidden="true"
        />
        <Input
          data-testid="faq-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t.faq.searchPlaceholder}
          className="h-12 pl-11 rounded-2xl font-sans text-base"
        />
      </div>

      {/* Category filter pills */}
      <div
        className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="faq-filter-pills"
      >
        {pills.map(p => (
          <button
            key={p.id}
            type="button"
            data-testid={`faq-filter-${p.id}`}
            onClick={() => setCategory(p.id)}
            className={`px-3.5 h-8 rounded-full text-xs font-sans font-semibold border transition-colors shrink-0 whitespace-nowrap ${
              category === p.id
                ? "bg-terracotta text-white border-terracotta"
                : "bg-surface-card text-ink-soft border-border hover:border-terracotta/50"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Results */}
      {filtered.length === 0 ? (
        <p
          data-testid="faq-no-results"
          className="text-sm text-ink-soft italic font-sans py-8 text-center"
        >
          {t.faq.noResults}
        </p>
      ) : (
        <div className="space-y-3">
          {filtered.map(f => (
            <FAQCard
              key={f.id}
              faq={f}
              isOpen={openId === f.id}
              onToggle={() => setOpenId(prev => prev === f.id ? null : f.id)}
            />
          ))}
        </div>
      )}

      {/* Footer: contact support for missing questions */}
      <div
        data-testid="faq-footer-support"
        className="pt-8 mt-4 border-t border-border/60 text-center"
      >
        <p className="text-sm sm:text-base text-ink font-sans">
          {t.common.faqNotFoundLead}{" "}
          <SupportMailLink variant="full" testid="faq-footer-support-link" subject="Domanda non presente nelle FAQ" />
        </p>
      </div>
    </div>
  );
}

function FAQCard({ faq, isOpen, onToggle }) {
  return (
    <section
      data-testid={`faq-card-${faq.id}`}
      data-open={isOpen ? "true" : "false"}
      className={`rounded-3xl border bg-surface-card overflow-hidden transition-colors ${
        isOpen ? "border-terracotta/40" : "border-border hover:border-terracotta/30"
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`faq-body-${faq.id}`}
        data-testid={`faq-toggle-${faq.id}`}
        className="w-full text-left p-5 sm:p-6 flex items-start gap-4"
      >
        <div className="h-6 w-1 rounded-full bg-terracotta shrink-0 mt-1" aria-hidden="true" />
        <h3 className="flex-1 font-display font-extrabold text-lg sm:text-xl text-ink tracking-tight leading-snug">
          {faq.question}
        </h3>
        <CaretDown
          size={20}
          weight="bold"
          className={`text-terracotta shrink-0 mt-1 transition-transform duration-300 ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
          aria-hidden="true"
        />
      </button>
      <div
        id={`faq-body-${faq.id}`}
        data-testid={`faq-body-${faq.id}`}
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-5 sm:px-6 pb-6 pt-1">
            <p className="text-sm sm:text-base text-ink font-sans leading-relaxed whitespace-pre-line">
              {faq.answer}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
